import { Box } from "@chakra-ui/react";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";

import ColorPicker from "../components/ColorPicker";
import BreachIcon from "../icons/breach_icon.svg?react";
import ManholeIcon from "../icons/manhole_icon.svg?react";
import PumpstationIcon from "../icons/pumpstation_icon.svg?react";
import WeirIcon from "../icons/weir_icon.svg?react";
import { setColor, setVisibility } from "../state/layerOneDMenu";
import { OneDElementLayerData } from "../utils/layerUtils";

import LayerSubMenu from "./LayerSubMenu";

interface Props {
  layerData: OneDElementLayerData;
  setVisibility: typeof setVisibility;
  setColor: typeof setColor;
}

function getFormattedMessageLayerName(layerName: string) {
  switch (layerName) {
    case "Added calculation node":
      return (
        <FormattedMessage
          id="layerMenuOneDElementLayer.addedCalculationNode"
          defaultMessage="Added calculation node"
        />
      );
    case "Breaches":
      return <FormattedMessage id="layerMenuOneDElementLayer.breaches" defaultMessage="Breaches" />;
    case "Channels":
      return <FormattedMessage id="layerMenuOneDElementLayer.channels" defaultMessage="Channels" />;
    case "Culverts":
      return <FormattedMessage id="layerMenuOneDElementLayer.culverts" defaultMessage="Culverts" />;
    case "Levees":
      return <FormattedMessage id="layerMenuOneDElementLayer.levees" defaultMessage="Levees" />;
    case "Manholes":
      return <FormattedMessage id="layerMenuOneDElementLayer.manholes" defaultMessage="Manholes" />;
    case "Nodes":
      return <FormattedMessage id="layerMenuOneDElementLayer.nodes" defaultMessage="Nodes" />;
    case "Orifices":
      return <FormattedMessage id="layerMenuOneDElementLayer.orifices" defaultMessage="Orifices" />;
    case "Sewers":
      return <FormattedMessage id="layerMenuOneDElementLayer.sewers" defaultMessage="Sewers" />;
    case "Pump stations":
      return (
        <FormattedMessage
          id="layerMenuOneDElementLayer.pumpStations"
          defaultMessage="Pump stations"
        />
      );
    case "Weirs":
      return <FormattedMessage id="layerMenuOneDElementLayer.weirs" defaultMessage="Weirs" />;
    default:
      return layerName;
  }
}

function getLegendIcon(type: string) {
  switch (type) {
    case "breaches":
      return <BreachIcon />;
    case "manholes":
      return <ManholeIcon />;
    // case "nodes":
    // For performance reasons nodes currently have no icon but use a circlemarker instead
    // legendIcon = <div style={{width: "20px", height: "20px"}}><NodeIcon /></div>;
    case "pumps":
      return <PumpstationIcon />;
    case "weirs":
      return <WeirIcon />;
    default:
      return null;
  }
}

const LayerMenuOneDElementLayer: React.FC<Props> = (props) => {
  const { layerData } = props;
  const legendIcon = getLegendIcon(layerData.type);
  const [showColorPickerModal, setColorPickerModal] = useState(false);
  const openColorPickerModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setColorPickerModal(true);
  };

  // Color picker for main categories in legend (for example sewers)
  const formattedMessageColorPicker = (
    <FormattedMessage id="model.color_picker" defaultMessage="Color Picker" />
  );
  const formattedMessageLayerName = getFormattedMessageLayerName(layerData.style.layerName);
  const colorPickerHeader = (
    <div>
      {formattedMessageColorPicker}
      {": "}
      {formattedMessageLayerName}
    </div>
  );

  return (
    <LayerSubMenu.Item
      active={layerData.style.visible}
      title={layerData.type}
      onClick={() => {
        props.setVisibility(layerData.type, !layerData.style.visible);
      }}
    >
      <LayerSubMenu.Item.Name>{formattedMessageLayerName}</LayerSubMenu.Item.Name>
      <Box>
        {legendIcon ? (
          <Box boxSize="14px">{legendIcon}</Box>
        ) : (
          <LayerSubMenu.Item.Color
            onClick={openColorPickerModal}
            value={layerData.style.color}
            isNode={layerData.style.layerName === "Nodes"}
          />
        )}
      </Box>

      {showColorPickerModal ? (
        <ColorPicker
          initialColor={layerData.style.color}
          onColorSave={(color: string) => props.setColor(layerData.type, color)}
          closeModal={() => setColorPickerModal(false)}
          header={colorPickerHeader}
        />
      ) : null}
    </LayerSubMenu.Item>
  );
};

export default connect(null, {
  setVisibility,
  setColor,
})(LayerMenuOneDElementLayer);
